import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import { Observable } from 'rxjs';
import { MasterdataEntityRepository } from '../repository/masterdata-entity.repository';
import {
  MasterdataEntityDefinition,
  MasterdataQueryOptions,
  MasterdataQueryResult,
} from '../types/masterdata-entity-definition';
import { MasterdataEntityRegistry } from './masterdata-entity-registry.service';

/** Programmatic access to any masterdata entity (the field-ok successor): pass a
 *  definition constant for typed results, or a registered entity name (untyped). */
@Injectable({ providedIn: 'root' })
export class MasterdataService {
  private readonly dataAccess = inject(ApiDataAccess);
  private readonly registry = inject(MasterdataEntityRegistry);

  /** Repository for a definition or registered name. Stateless and cheap. */
  repository<TDto>(
    entity: MasterdataEntityDefinition<TDto> | string
  ): MasterdataEntityRepository<TDto> {
    const definition =
      typeof entity === 'string'
        ? (this.registry.get(entity) as MasterdataEntityDefinition<TDto>)
        : entity;
    return new MasterdataEntityRepository(this.dataAccess, definition);
  }

  search$<TDto>(
    entity: MasterdataEntityDefinition<TDto> | string,
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Observable<TDto[]> {
    return this.repository(entity).search$(searchTerm, options);
  }

  search<TDto>(
    entity: MasterdataEntityDefinition<TDto> | string,
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Promise<TDto[]> {
    return this.repository(entity).search(searchTerm, options);
  }

  searchWithCount$<TDto>(
    entity: MasterdataEntityDefinition<TDto> | string,
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Observable<MasterdataQueryResult<TDto>> {
    return this.repository(entity).searchWithCount$(searchTerm, options);
  }

  searchWithCount<TDto>(
    entity: MasterdataEntityDefinition<TDto> | string,
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Promise<MasterdataQueryResult<TDto>> {
    return this.repository(entity).searchWithCount(searchTerm, options);
  }
}
